import React, { useState, useEffect } from 'react';
import SportMatchesPage from './SportMatchesPage';
import { BetItem } from './BettingSlipDrawer';
import { getSports } from '../api/api';
import { SportItem } from '../api/types';

const SPORT_EMOJI: Record<string, string> = {
  Soccer: '⚽',
  Basketball: '🏀',
  Tennis: '🎾',
  'Ice Hockey': '🏒',
  Handball: '🤾',
  Volleyball: '🏐',
  Baseball: '⚾',
  'American Football': '🏈',
  'Aussie Rules': '🏉',
  'Table Tennis': '🏓',
  Cricket: '🏏',
  Darts: '🎯',
  Badminton: '🏸',
  Boxing: '🥊',
  MMA: '🥋',
  Rugby: '🏉',
  Futsal: '⚽',
  Snooker: '🎱',
  Golf: '⛳',
  Floorball: '🏒',
  eSoccer: '🎮',
  eBasketball: '🎮',
  'ESport Counter-Strike': '🎮',
  'ESport League of Legends': '🎮',
  'ESport Dota': '🎮',
  'ESport Call of Duty': '🎮',
  Squash: '🎾',
};

const TOP_SPORTS = ['Soccer', 'Basketball', 'Tennis', 'Ice Hockey', 'Handball', 'Volleyball'];

const timeFilters = [
  { id: 'live', label: 'Live', image: '/images/live_time.png' },
  { id: 'heute', label: 'Heute', image: '/images/haute.png' },
  { id: '3std', label: '3 Std.', image: '/images/3-hours.png' },
  { id: '48std', label: '48 Std.', image: '/images/48-hours.png' },
];



interface SportEntry {
  id: string;
  label: string;
  emoji: string;
  count: number;
  sportId: string;
}

interface SportPageProps {
  onLoginClick: () => void;
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
}

const SportPage: React.FC<SportPageProps> = ({ onLoginClick, bets, onAddBet, onRemoveBet }) => {
  const [activeTime, setActiveTime] = useState('live');
  const [search, setSearch] = useState('');
  const [selectedSport, setSelectedSport] = useState<SportEntry | null>(null);
  const [sports, setSports] = useState<SportItem[]>([]);
  const [loadingSports, setLoadingSports] = useState(true);

  useEffect(() => {
    getSports()
      .then((r) => setSports(r.items))
      .catch(() => {})
      .finally(() => setLoadingSports(false));
  }, []);

  const toEntry = (s: SportItem): SportEntry => ({
    id: s.sport_id,
    sportId: s.sport_id,
    label: s.sport_name,
    emoji: SPORT_EMOJI[s.sport_name] ?? '🏅',
    count: s.event_count,
  });

  const topSports: SportEntry[] = sports
    .filter((s) => TOP_SPORTS.includes(s.sport_name))
    .sort((a, b) => b.event_count - a.event_count)
    .map(toEntry);

  const azSports: SportEntry[] = sports
    .filter((s) => !TOP_SPORTS.includes(s.sport_name))
    .sort((a, b) => a.sport_name.localeCompare(b.sport_name))
    .map(toEntry);

  const filtered = (list: SportEntry[]) =>
    list.filter((s) => s.label.toLowerCase().includes(search.toLowerCase()));

  if (selectedSport) {
    return (
      <SportMatchesPage
        sportLabel={selectedSport.label}
        sportEmoji={selectedSport.emoji}
        sportId={selectedSport.sportId}
        timeFilter={activeTime}
        onBack={() => setSelectedSport(null)}
        onLoginClick={onLoginClick}
        bets={bets}
        onAddBet={onAddBet}
        onRemoveBet={onRemoveBet}
      />
    );
  }

  if (loadingSports) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Red Header */}
      <div className="bg-red-700 px-4 pt-4 pb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-white text-2xl font-bold">Sport</h1>
          <button
            onClick={onLoginClick}
            className="border border-white text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-white hover:text-red-700 transition"
          >
            Anmelden
          </button>
        </div>
        {/* Search */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🔍</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Team, Event oder Wettbewerb"
            className="w-full bg-white rounded-full pl-10 pr-4 py-3 text-sm text-gray-700 focus:outline-none"
          />
        </div>
      </div>

      {/* Time Filter Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex gap-4 overflow-x-auto w-full">
        {timeFilters.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveTime(f.id)}
            className={`flex flex-col items-center gap-1 w-1/4 pb-1 border-b-2 transition ${
              activeTime === f.id ? 'border-red-600' : 'border-transparent'
            }`}
          >
            <div className="relative w-10 h-10 flex items-center justify-center">
              <img src={f.image} alt={f.label} className="w-10 h-10 object-contain" />
            </div>
            <span className={`text-xs font-medium whitespace-nowrap ${
              activeTime === f.id ? 'text-gray-900' : 'text-gray-500'
            }`}>{f.label}</span>
          </button>
        ))}
      </div>

      {/* Sport Lists */}
      <div className="px-4 pt-4">
        {/* Top Section */}
        {filtered(topSports).length > 0 && (
          <>
            <h2 className="text-base font-bold text-gray-900 mb-2">Top</h2>
            <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-5">
              {filtered(topSports).map((sport, idx, arr) => (
                <button
                  key={sport.id}
                  onClick={() => setSelectedSport(sport)}
                  className={`w-full flex items-center gap-4 px-4 py-4 hover:bg-gray-50 transition text-left ${
                    idx < arr.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <span className="text-2xl w-8 text-center">{sport.emoji}</span>
                  <span className="flex-1 text-sm font-medium text-gray-800">{sport.label}</span>
                  <span className="text-sm text-gray-500">{sport.count}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* A-Z Section */}
        {filtered(azSports).length > 0 && (
          <>
            <h2 className="text-base font-bold text-gray-900 mb-2">A-Z</h2>
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              {filtered(azSports).map((sport, idx, arr) => (
                <button
                  key={sport.id}
                  onClick={() => setSelectedSport(sport)}
                  className={`w-full flex items-center gap-4 px-4 py-4 hover:bg-gray-50 transition text-left ${
                    idx < arr.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <span className="text-2xl w-8 text-center">{sport.emoji}</span>
                  <span className="flex-1 text-sm font-medium text-gray-800">{sport.label}</span>
                  <span className="text-sm text-gray-500">{sport.count}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {filtered(topSports).length === 0 && filtered(azSports).length === 0 && (
          <p className="text-center text-gray-500 text-sm mt-10">Keine Ergebnisse gefunden.</p>
        )}
      </div>
    </div>
  );
};

export default SportPage;
